// WAAP Advanced Protection → Cross-Site Request Forgery (CSRF) Protection.
//
// Per-website feature stored inside the site-config object under
// `config.waf_csrf_strategy`. Backed by the PRIVATE portal /site(s) via
// eagle_waf/website (read-modify-write, like Page Defacement). Account-level: the
// `:id` domain only resolves the customer session; a site is addressed by its
// portal site-config id. So the hooks take an ANCHOR domain UUID:
//
//   GET /waap/domains/:id/csrf            → portal GET /sites
//   GET /waap/domains/:id/csrf/:siteId    → portal GET /site/:siteId
//   PUT /waap/domains/:id/csrf/:siteId    → portal PUT /site (patch config.waf_csrf_strategy)

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

// ---- Types ----------------------------------------------------------------

/** One referer-check entry in the CSRF URL list. */
export interface CsrfRule {
  path: string;
  /** ALL | GET | POST | … (CSRF_METHODS). */
  method: string;
  case_sensitive: boolean;
  comment: string;
}

/** A CSRF Protection rule (the site's `config.waf_csrf_strategy` subset). */
export interface CsrfConfig {
  enabled: boolean;
  /** Trusted referers allowed to cross-site (csrf_whitelist). */
  whitelist: string;
  rules: CsrfRule[];
}

/** A row in the CSRF Protection website list. */
export interface CsrfSite {
  site_id: number;
  domain: string;
  enabled: boolean;
}

/** HTTP-method options for a CSRF URL entry. */
export const CSRF_METHODS = ['ALL', 'GET', 'POST', 'PUT', 'DELETE', 'OPTIONS', 'HEAD', 'PATCH'] as const;

/** Defaults for a fresh CSRF rule. */
export function defaultCsrf(): CsrfConfig {
  return { enabled: false, whitelist: '', rules: [] };
}

// ---- Paths + query keys --------------------------------------------------

function basePath(domainId: string): string {
  return `/waap/domains/${domainId}/csrf`;
}

export const csrfKeys = {
  all: ['waap', 'csrf'] as const,
  sites: (domainId: string) => ['waap', 'csrf', domainId, 'sites'] as const,
  site: (domainId: string, siteId: string | number) =>
    ['waap', 'csrf', domainId, 'site', String(siteId)] as const,
};

// ---- Hooks ---------------------------------------------------------------

/** List every website with its CSRF Protection enabled flag. */
export function useCsrfSites(domainId: string | undefined) {
  return useQuery({
    queryKey: csrfKeys.sites(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<CsrfSite[]> => {
      const res = await serviceApiV3.get<CsrfSite[] | { data: CsrfSite[] }>(
        basePath(domainId as string),
      );
      const body = res.data as CsrfSite[] | { data?: CsrfSite[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Fetch one site's CSRF Protection config (for the edit form). */
export function useCsrf(domainId: string | undefined, siteId: string | undefined) {
  return useQuery({
    queryKey: csrfKeys.site(domainId ?? '', siteId ?? ''),
    enabled: !!domainId && !!siteId,
    retry: false,
    queryFn: async (): Promise<CsrfConfig> => {
      const res = await serviceApiV3.get<CsrfConfig | { data: CsrfConfig }>(
        `${basePath(domainId as string)}/${siteId}`,
      );
      const body = res.data as CsrfConfig | { data?: CsrfConfig };
      return (body as { data?: CsrfConfig })?.data ?? (body as CsrfConfig);
    },
  });
}

/** Update one site's CSRF Protection config. */
export function useUpdateCsrf(
  domainId: string,
  siteId: number | string,
  options?: UseMutationOptions<unknown, unknown, CsrfConfig>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CsrfConfig) => {
      const res = await serviceApiV3.put(`${basePath(domainId)}/${siteId}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: csrfKeys.sites(domainId) });
      qc.invalidateQueries({ queryKey: csrfKeys.site(domainId, siteId) });
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Service-scoped twins (account-level, work with zero websites) ----------

function serviceBasePath(serviceId: string): string {
  return `/services/${serviceId}/waap/csrf`;
}

export const csrfServiceKeys = {
  sites: (serviceId: string) => ['waap', 'csrf', 'service', serviceId, 'sites'] as const,
  site: (serviceId: string, siteId: string | number) =>
    ['waap', 'csrf', 'service', serviceId, 'site', String(siteId)] as const,
};

/** List every website with its CSRF Protection enabled flag, service-scoped. */
export function useServiceCsrfSites(serviceId: string | undefined) {
  return useQuery({
    queryKey: csrfServiceKeys.sites(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<CsrfSite[]> => {
      const res = await serviceApiV3.get<CsrfSite[] | { data: CsrfSite[] }>(
        serviceBasePath(serviceId as string),
      );
      const body = res.data as CsrfSite[] | { data?: CsrfSite[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Fetch one site's CSRF Protection config, service-scoped. */
export function useServiceCsrf(serviceId: string | undefined, siteId: string | undefined) {
  return useQuery({
    queryKey: csrfServiceKeys.site(serviceId ?? '', siteId ?? ''),
    enabled: !!serviceId && !!siteId,
    retry: false,
    queryFn: async (): Promise<CsrfConfig> => {
      const res = await serviceApiV3.get<CsrfConfig | { data: CsrfConfig }>(
        `${serviceBasePath(serviceId as string)}/${siteId}`,
      );
      const body = res.data as CsrfConfig | { data?: CsrfConfig };
      return (body as { data?: CsrfConfig })?.data ?? (body as CsrfConfig);
    },
  });
}

/** Update one site's CSRF Protection config, service-scoped. */
export function useUpdateServiceCsrf(
  serviceId: string,
  siteId: number | string,
  options?: UseMutationOptions<unknown, unknown, CsrfConfig>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CsrfConfig) => {
      const res = await serviceApiV3.put(`${serviceBasePath(serviceId)}/${siteId}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: csrfServiceKeys.sites(serviceId) });
      qc.invalidateQueries({ queryKey: csrfServiceKeys.site(serviceId, siteId) });
      options?.onSuccess?.(...args);
    },
  });
}
